import { Slider } from '@syncfusion/ej2-inputs/slider';
import { ContextMenu } from '@syncfusion/ej2-navigations/context-menu';
import { TreeView } from '@syncfusion/ej2-navigations/treeview';
import { containsCoordinate, getCenter } from 'ol/extent';
import Image from 'ol/layer/Image';
import ImageArcGISRest from 'ol/source/ImageArcGISRest';

import Mediator from 'gisw/Classes/Mediator';
import { fetchSubFeature } from 'gisw/Helpers/arcgis/feature-layer-utils';
// @ts-ignore
import auth from 'gisw/Helpers/auth';
import messageObserver from 'gisw/Widgets/MessageManager/MessageObserver';

import './toc.less';

class LayersManager {
  /** @type {string} */
  token = null;

  /**
   * @param {object} config
   * @param {string} config.id
   */
  constructor (config) {
    this.id = config.id;

    /** @type {Record<string, HTMLDivElement>} */
    this.widgetItems = {};
    /** @type {Record<string, string>} */
    this.tocWidgetsReplace = {};
    this.isFirstLoad = true;
    this.loadLayers();
  }

  /**
   * @param {number} id
   */
  async getFeatureLayerById (id) {
    const layer = this.getCurrentLayer();
    /** @type {string} */
    // @ts-ignore
    const layerUrl = layer.getSource().getUrl() + '/' + id;

    if (this.token === null) {
      this.token = await Mediator.mapLayerCredientials.getMapLayerAuthenticated(
        'feature'
      );
    }

    return fetchSubFeature(layerUrl, this.token);
  }

  getCurrentLayer () {
    const curTab = Mediator.getCurrentTabName();
    return this.mapLayers.dynamicLayers[this.formatSub(curTab)];
  }

  getAvailableChildLayers () {
    const layer = this.getCurrentLayer();
    return this._getAvailableChildLayers(layer.treeData);
  }

  /**
   * @private
   * @param {*} data
   */
  _getAvailableChildLayers (data) {
    /** @type {Array} */
    let results = [];

    for (let i = 0; i < data.length; i++) {
      const layerInfo = data[i];

      if (layerInfo.subLayerIds && layerInfo._subLayerInfos) {
        results = results.concat(
          this._getAvailableChildLayers(layerInfo._subLayerInfos)
        );
      } else {
        results.push(layerInfo);
      }
    }

    return results;
  }

  /**
   * @param {string} tabName
   * @returns {HTMLDivElement}
   */
  getTOC (tabName) {
    if (!this.mapLayers.dynamicLayers[tabName]) {
      Mediator.jstManager.jstError(
        'Widget',
        'LayersManager',
        'getTOC',
        'Dynamic Map layer for ' + tabName + ' is not found.'
      );
      return null;
    }

    return this.loadTOC(tabName);
  }

  /**
   * @private
   * @param {string} tabName
   */
  switchLayersForTab (tabName) {
    const tabKey = this.formatSub(tabName);

    if (this.curLayer) {
      Mediator.map.removeLayer(this.curLayer);
    }

    this.curLayer = this.mapLayers.dynamicLayers[tabKey];
    Mediator.map.addLayer(this.curLayer);

    if (Mediator.map) {
      this.zoomToCurrentLayerFullExtent(this.isFirstLoad);
      this.isFirstLoad = false;
    }
  }

  removeAllSubMapLayers () {
    for (const layerKey in this.mapLayers.dynamicLayers) {
      if (
        this.mapLayers.dynamicLayers.hasOwnProperty(layerKey) &&
        layerKey.indexOf('sub') >= 0
      ) {
        this.mapLayers.dynamicLayers[layerKey] = null;
      }
    }
  }

  removeLayerFromMap (layerName) {
    if (!Mediator.map) {
      return;
    }

    const oldLayer = this.mapLayers.dynamicLayers[layerName];

    if (oldLayer) {
      Mediator.map.removeLayer(oldLayer);
    }
  }

  /**
   * @param {boolean} alwaysZoom
   */
  zoomToCurrentLayerFullExtent (alwaysZoom) {
    const curLayer = this.getCurrentLayer();

    if (curLayer && !curLayer.loaded) {
      curLayer.once('load', () => {
        this.zoomToLayer(curLayer, alwaysZoom);
      });
    } else {
      this.zoomToLayer(curLayer, alwaysZoom);
    }
  }

  /**
   * @param {Image} curLayer
   * @param {boolean} alwaysZoom
   */
  zoomToLayer (curLayer, alwaysZoom) {
    if (!curLayer) {
      return;
    }

    const view = Mediator.map.getView();
    const mapProjCode = view.getProjection().getCode();
    const layerProjCode =
      'EPSG:' + curLayer.fullExtent.spatialReference.latestWkid;

    if (mapProjCode !== layerProjCode) {
      Mediator.jstManager.jstWarn(
        'Tool',
        'LayersManager',
        'zoomToCurrentLayerFullExtent',
        'Failed to zoom to an extent with different wkid than the map'
      );
      return;
    }

    const fitParams = { nearest: true };
    /** @type {import('ol/extent').Extent} */
    const layerExtent = [
      curLayer.fullExtent.xmin,
      curLayer.fullExtent.ymin,
      curLayer.fullExtent.xmax,
      curLayer.fullExtent.ymax
    ];
    const mapCentered = containsCoordinate(
      layerExtent,
      getCenter(view.calculateExtent())
    );

    if (!alwaysZoom && mapCentered) {
      return;
    }

    if (alwaysZoom && !this.isFirstLoad) {
      fitParams.duration = 200;
    }

    view.fit(layerExtent, fitParams);
  }

  loadLayers () {
    let layer;
    const tablayers = Mediator.serverData.TabLayers;
    const tabSublayers = Mediator.serverData.TabSubLayers;
    /** @type {Record<string, Image>} */
    const dynamicLayers = {};

    for (const tab in tablayers) {
      if (tablayers.hasOwnProperty(tab)) {
        layer = this.getDynamicMapServiceLayer(tab, tablayers[tab]);
        dynamicLayers[tab] = layer;
      }
    }

    for (const tabSub in tabSublayers) {
      if (tabSublayers.hasOwnProperty(tabSub)) {
        layer = this.getDynamicMapServiceLayer(
          tabSub + 'sub',
          tabSublayers[tabSub],
          true
        );
        dynamicLayers[tabSub + 'sub'] = layer;
      }
    }

    this.mapLayers = {
      dynamicLayers
    };
  }

  processLoginData (curTabInfo) {
    let layer;
    const curTabName = curTabInfo.TabName;
    const subTabName = curTabName + 'sub';

    if (
      curTabInfo.MapService !== null &&
      !this.mapLayers.dynamicLayers[curTabName]
    ) {
      layer = this.getDynamicMapServiceLayer(curTabName, curTabInfo.MapService);
      this.mapLayers.dynamicLayers[curTabName] = layer;
    }

    if (
      curTabInfo.SubMapService !== null &&
      !this.mapLayers.dynamicLayers[subTabName]
    ) {
      layer = this.getDynamicMapServiceLayer(
        subTabName,
        curTabInfo.SubMapService
      );
      this.mapLayers.dynamicLayers[subTabName] = layer;
    }
  }

  /**
   * @param {string} layerKey
   * @param {string} layerurl
   * @param {boolean} [subs]
   */
  // eslint-disable-next-line class-methods-use-this
  getDynamicMapServiceLayer (layerKey, layerurl, subs = false) {
    const isSub = subs || !!Mediator.serverData.FrontEndOption.BoltonMenk;
    const credential = Mediator.mapLayerCredientials.credentials?.[0];
    const token = isSub ? credential?.token ?? '' : '';
    const source = new ImageArcGISRest({
      crossOrigin: 'anonymous',
      hidpi: false,
      url: layerurl,
      ratio: 1,
      params: { token }
    });

    source.on('imageloadstart', () => {
      Mediator.handle({ name: 'loading.show' });
    });

    source.on('imageloadend', () => {
      Mediator.handle({ name: 'loading.hide' });
    });

    if (isSub) {
      source.on('imageloaderror', () => {
        if (Mediator.mapLayerCredientials?.isCredientialsExpired()) {
          Mediator.jstManager.jstWarn(
            'Data',
            'Mediator',
            'getDynamicMapServiceLayer0',
            'Sub Map layer is failed to load, crediential expired. ' +
              'Attempt to re-authenticate.'
          );

          Mediator.mapLayerCredientials.getMapLayerAuthenticated('submap');
        } else {
          // when sub map layer failed to load, regualar map layer is used.
          const dynamicLayerKey = layerKey.replace('sub', '');

          this.mapLayers.dynamicLayers[layerKey] = null;
          this.tocWidgetsReplace[layerKey] = dynamicLayerKey;
          Mediator.jstManager.jstWarn(
            'Data',
            'Mediator',
            'getDynamicMapServiceLayer1',
            'Map layer \'' +
              layerKey +
              '\' is failed to load, and it\'s replaced with \'' +
              dynamicLayerKey +
              '\''
          );

          // Refreshing the current tab.
          Mediator.currentTab = '';
          Mediator.handle({
            name: 'centerMenu.tab.click',
            args: [{ name: Mediator.getCurrentTabName() }]
          });
        }
      });
    }

    const layer = new Image({ source });

    if (isSub) {
      layer.set('credential', credential);
    }

    layer.setVisibleLayers = function (visibleLayers) {
      const params = source.getParams();
      params.LAYERS = 'show:' + visibleLayers.join(',');
      source.updateParams(params);
      layer.setVisible(visibleLayers.length);
    };

    return layer;
  }

  /**
   * @param {string} name
   */
  formatSub (name) {
    return auth.loggedIn && this.mapLayers.dynamicLayers[name + 'sub']
      ? name + 'sub'
      : name;
  }

  /**
   * @private
   * @param {string} btnName
   */
  loadTOC (btnName) {
    let btnKey = this.formatSub(btnName);
    btnKey = this.tocWidgetsReplace[btnKey] || btnKey;

    if (!this.widgetItems[btnKey]) {
      const container = document.createElement('div');
      container.id = btnKey;
      container.classList.add('toc');

      this.widgetItems[btnKey] = container;

      this._getLegendInfo(btnKey)
        .then(data => {
          this.switchLayersForTab(btnName);
          fillTOC(container, this.mapLayers.dynamicLayers[btnKey], data);
        })
        .catch(err => {
          messageObserver.broadcast({
            title: 'Error',
            message: 'There was an issue getting legend information.'
          });

          Mediator.jstManager.jstError(
            'Widget',
            'LayersManager',
            'loadTOC',
            err ? JSON.stringify(err) : ''
          );
        });
    } else {
      this.switchLayersForTab(btnName);
    }

    return this.widgetItems[btnKey];
  }

  /**
   * @private
   * @param {string} btnKey
   */
  async _getLegendInfo (btnKey) {
    const layer = this.mapLayers.dynamicLayers[btnKey];

    const urlTokenObj = await Mediator.mapLayerCredientials.getAuthenticatedLayerURLandToken(
      layer
    );

    const data = await new Promise((resolve, reject) => {
      $.ajax(urlTokenObj.url, {
        data: { f: 'json', token: urlTokenObj.token },
        dataType: 'json',
        error: err => {
          reject(err);
        },
        success: data => {
          resolve(data);
        }
      });
    });

    return this._processServiceInfo(urlTokenObj, layer, data);
  }

  /**
   * @private
   * @param {any} urlTokenObj
   * @param {Image} layer
   * @param {any} data
   */
  _processServiceInfo (urlTokenObj, layer, data) {
    if (data.error) {
      messageObserver.broadcast({
        title: 'Error',
        message:
          'There was an issue getting legend information.<br />' +
          data.error.message
      });

      return Promise.reject(Error(data.error.message));
    }

    layer.layerInfos = data.layers;
    layer.fullExtent = data.fullExtent;
    layer.initialExtent = data.initialExtent;
    layer.defaultVisibleLayers = data.layers.reduce(function (
      result,
      layerInfo
    ) {
      if (layerInfo.defaultVisibility) {
        result.push(layerInfo.id);
      }

      return result;
    },
    []);

    return new Promise((resolve, reject) => {
      $.ajax(urlTokenObj.url + '/legend', {
        data: { f: 'json', token: urlTokenObj.token },
        dataType: 'json',
        error: err => {
          reject(err);
        },
        success: data => {
          resolve(data);
        }
      });
    }).then(data => this._processLegendInfo(layer, data));
  }

  /**
   * @private
   * @param {Image} layer
   * @param {any} json
   */
  _processLegendInfo (layer, json) {
    const hiddenLayers = this._getHiddenLayers(layer);
    const layerLookup = layer.layerInfos.reduce((result, layerInfo) => {
      result[layerInfo.id] = layerInfo;
      return result;
    }, {});

    // Since we reuse the same layer object for creating multiple TOC
    // Restore the visible layers from the map serviceLayers
    // when making a new TOC
    if (!layer.defaultVisibleLayers) {
      layer.defaultVisibleLayers = layer.visibleLayers.slice();
    } else {
      layer.visibleLayers = layer.defaultVisibleLayers.slice();
    }

    this._AddIconData(json, layerLookup);

    const treeData = this._ProcessTreeData(layer, layerLookup, hiddenLayers);

    layer.setVisibleLayers(layer.visibleLayers);

    return Promise.resolve([treeData, layerLookup]);
  }

  /**
   * @private
   * @param {Image} layer
   */
  // eslint-disable-next-line class-methods-use-this
  _getHiddenLayers (layer) {
    const hiddenLayers = [];
    let i;

    if (Mediator.serverData.entityType === 'eCity') {
      // Default to no access if not explicitly set yet
      for (i = 0; i < layer.layerInfos.length; i++) {
        if (!layer.layerInfos[i].subLayerIds) {
          hiddenLayers[layer.layerInfos[i].id] = true;
        }
      }
    }

    for (i = 0; i < Mediator.layerConfigs.length; i++) {
      const config = Mediator.layerConfigs[i];
      hiddenLayers[config.LayerID] = !config.IsVisible;
    }

    return hiddenLayers;
  }

  /**
   * @private
   * @param {*} json
   * @param {*} layerLookup
   */
  _AddIconData (json, layerLookup) {
    if (!json.layers) {
      return;
    }

    // attach legend Info to layer info
    for (let i = 0; i < json.layers.length; i++) {
      const legInfo = json.layers[i];
      const layerInfo = layerLookup[legInfo.layerId];

      if (legInfo.legend.length === 1) {
        layerInfo.icon =
          'data:image/png;base64, ' + legInfo.legend[0].imageData;
      }

      if (legInfo.legend.length > 1) {
        layerInfo._subLayerInfos = [];

        for (let j = 0; j < legInfo.legend.length; j++) {
          const curLegInfo = legInfo.legend[j];
          layerInfo._subLayerInfos.push({
            treeId: layerInfo.id + 1 + '_' + j,
            name: curLegInfo.label,
            icon: 'data:image/png;base64, ' + curLegInfo.imageData,
            htmlAttributes: { 'class': 'nocheckbox' }
          });
        }
      }
    }
  }

  /**
   * @private
   * @param {Image} layer
   * @param {Record<string, any>} layerLookup
   * @param {boolean[]} hiddenLayers
   */
  _ProcessTreeData (layer, layerLookup, hiddenLayers) {
    const treeData = [];

    for (let i = 0; i < layer.layerInfos.length; i++) {
      const layerInfo = layer.layerInfos[i];

      layerInfo.treeId = layerInfo.id + 1;
      layerInfo.isChecked = layerInfo.defaultVisibility;
      layerInfo.expanded = layerInfo.defaultVisibility;

      let parent = layerLookup[layerInfo.parentLayerId];
      let inactiveParent = false;

      while (!inactiveParent && parent) {
        inactiveParent = !parent.defaultVisibility;
        parent = layerLookup[parent.parentLayerId];
      }

      this._nestLayerInfos(layerInfo, layerLookup, hiddenLayers);

      // Remove parents, children with inactive parents,
      // and hidden layers from visible layer list
      if (
        layerInfo.subLayerIds ||
        inactiveParent ||
        hiddenLayers[layerInfo.id]
      ) {
        const visibleIndex = layer.visibleLayers.indexOf(layerInfo.id);

        if (visibleIndex >= 0) {
          layer.visibleLayers.splice(visibleIndex, 1);
        }
      }

      if (this._isValidTreeData(layerInfo, hiddenLayers)) {
        treeData.push(layerInfo);
      }
    }

    return treeData;
  }

  /**
   * @private
   * @param {*} layerInfo
   * @param {*} layerLookup
   * @param {*} hiddenLayers
   */
  _nestLayerInfos (layerInfo, layerLookup, hiddenLayers) {
    if (!layerInfo.subLayerIds) {
      return;
    }

    const subLayerInfos = [];

    for (let j = 0; j < layerInfo.subLayerIds.length; j++) {
      const id = layerInfo.subLayerIds[j];
      const subLayer = layerLookup[id];

      if (
        !hiddenLayers[id] &&
        this._isValidGroupLayer(subLayer, layerLookup, hiddenLayers)
      ) {
        subLayerInfos.push(subLayer);
      }
    }

    layerInfo._subLayerInfos = subLayerInfos;
  }

  /**
   * @private
   * @param {*} subLayer
   * @param {*} layerLookup
   * @param {*} hiddenLayers
   * @returns {boolean}
   */
  _isValidGroupLayer (subLayer, layerLookup, hiddenLayers) {
    if (!subLayer.subLayerIds) {
      return true;
    }

    let hasVisibleSubLayer = false;

    for (let i = 0; i < subLayer.subLayerIds.length; i++) {
      const subLayerInfo = layerLookup[subLayer.subLayerIds[i]];

      if (subLayerInfo.subLayerIds) {
        hasVisibleSubLayer =
          hasVisibleSubLayer ||
          this._isValidGroupLayer(subLayerInfo, layerLookup, hiddenLayers);
      } else {
        hasVisibleSubLayer =
          hasVisibleSubLayer || !hiddenLayers[subLayerInfo.id];
      }
    }

    return hasVisibleSubLayer;
  }

  /**
   * @private
   * @param {*} layerInfo
   * @param {*} hiddenLayers
   */
  _isValidTreeData (layerInfo, hiddenLayers) {
    return (
      layerInfo.parentLayerId === -1 &&
      (layerInfo._subLayerInfos === undefined ||
        layerInfo._subLayerInfos.length) &&
      !hiddenLayers[layerInfo.id]
    );
  }
}

/**
 * @param {HTMLDivElement} container
 * @param {Image} layer
 * @param {any[]} results
 */
function fillTOC (container, layer, results) {
  layer.treeData = results[0];

  const layerLookup = results[1];

  // Wait until we have treeData saved
  layer.loaded = true;
  layer.dispatchEvent('load');

  const tocTitle = document.createElement('div');
  tocTitle.classList.add('toc__title');
  tocTitle.textContent = Mediator.serverData.TabLabel[Mediator.currentTab];
  container.appendChild(tocTitle);

  _makeSlider(container, layer);

  const treeObj = _makeTreeView(container, layer, layerLookup);
  _makeContextMenu(container, treeObj, layer, layerLookup);
}

/**
 * @param {HTMLDivElement} container
 * @param {Image} layer
 */
function _makeSlider (container, layer) {
  const sliderContainer = document.createElement('div');
  sliderContainer.id = container.id + '--slider';
  sliderContainer.style.marginLeft = '30px';
  container.appendChild(sliderContainer);

  return new Slider(
    {
      min: 0,
      value: layer.getOpacity(),
      max: 1,
      step: 0.01,
      change (args) {
        // @ts-ignore
        layer.setOpacity(args.value);
      },
      width: '175px'
    },
    sliderContainer
  );
}

/**
 * @param {HTMLDivElement} container
 * @param {Image} layer
 * @param {*} layerLookup
 */
function _makeTreeView (container, layer, layerLookup) {
  const treeContainer = document.createElement('div');
  treeContainer.id = container.id + '--tree';
  container.appendChild(treeContainer);

  const treeObj = new TreeView(
    {
      cssClass: 'custom-treeview',
      fields: {
        dataSource: layer.treeData,
        id: 'treeId',
        text: 'name',
        child: '_subLayerInfos',
        imageUrl: 'icon'
      },
      showCheckBox: true,
      autoCheck: false,
      nodeChecked (args) {
        _onNodeChecked.call(this, layer, layerLookup, args);
      },

      /**
       * @this {TreeView}
       */
      nodeClicked (args) {
        if (
          args.event.button === 2 ||
          // @ts-ignore
          !args.event.target.classList.contains('e-fullrow') ||
          args.node.classList.contains('nocheckbox')
        ) {
          return;
        }

        if (this.getNodeData(args.node).isChecked === 'true') {
          this.uncheckAll([args.node]);
        } else {
          this.checkAll([args.node]);
        }
      },
      expandOn: 'None'
    },
    treeContainer
  );

  treeObj.animation.expand.duration = 200;
  treeObj.animation.collapse.duration = 200;

  return treeObj;
}

/**
 * @param {HTMLDivElement} container
 * @param {TreeView} treeObj
 * @param {Image} layer
 * @param {*} layerLookup
 */
function _makeContextMenu (container, treeObj, layer, layerLookup) {
  // Prevent duplicates >:()
  const menuId = container.id + 'ContextMenu' + Date.now();

  const menuContainer = document.createElement('ul');
  menuContainer.id = menuId;
  container.appendChild(menuContainer);

  let selectedNode = null;

  return new ContextMenu(
    {
      beforeOpen: args => {
        const target = args.event.target;

        if (!(target instanceof HTMLElement)) {
          return;
        }

        const listItem = target.closest('.e-list-item');

        selectedNode = treeObj.getNodeData(listItem);
      },
      target: '#' + container.id + '--tree',
      items: [{ text: 'Zoom to Layer' }],
      select: () => {
        _onContextMenuSelect(selectedNode, layer, layerLookup);
      }
    },
    menuContainer
  );
}

/**
 * @param {*} selectedNode
 * @param {Image} layer
 * @param {*} layerLookup
 */
function _onContextMenuSelect (selectedNode, layer, layerLookup) {
  const layerId = parseInt(selectedNode.id) - 1;

  if (layerLookup[layerId] && layerLookup[layerId].extent) {
    Mediator.map.getView().fit(layerLookup[layerId].extent, { duration: 200 });
    return;
  }

  Mediator.mapLayerCredientials
    .getAuthenticatedLayerURLandToken(layer)
    .then(urlTokenObj => {
      const url = urlTokenObj.url + '/' + layerId;

      $.ajax(url, {
        data: { f: 'json', token: urlTokenObj.token },
        dataType: 'json',
        success: zoomLayer => {
          if (zoomLayer.extent) {
            layerLookup[layerId].extent = [
              zoomLayer.extent.xmin,
              zoomLayer.extent.ymin,
              zoomLayer.extent.xmax,
              zoomLayer.extent.ymax
            ];
            Mediator.map
              .getView()
              .fit(layerLookup[layerId].extent, { duration: 200 });
          }
        }
      });
    });
}

/**
 * @this {TreeView} Scoped to tree view object
 * @param {*} layer
 * @param {*} layerLookup
 * @param {*} args
 */
function _onNodeChecked (layer, layerLookup, args) {
  const node = args.data[0];
  const layerId = +node.id - 1;
  const layerInfo = layerLookup[layerId];
  const inactiveParent =
    this.getNode('' + (layerInfo.parentLayerId + 1)).isChecked === 'false';
  const isChecked = node.isChecked === 'true';

  // Group Layers and Layers with multiple symbologies
  if (layerInfo._subLayerInfos) {
    if (isChecked) {
      this.expandAll([node.id]);
    } else {
      this.collapseAll([node.id]);
    }
  }

  if (inactiveParent) {
    return;
  }

  const updatedLayerIds = _getUpdatedLayerIds.call(
    this,
    layerInfo,
    layerLookup
  );

  _updateVisibleLayers(layer.visibleLayers, updatedLayerIds, isChecked);

  layer.setVisibleLayers(layer.visibleLayers);
}

/**
 * @this {TreeView}
 * @param {*} layerInfo
 * @param {*} layerLookup
 */
function _getUpdatedLayerIds (layerInfo, layerLookup) {
  const results = [];

  // Group layer
  if (layerInfo.subLayerIds) {
    const checkedNodes = this.getAllCheckedNodes();
    _addSubLayers(
      results,
      layerInfo.id,
      layerInfo.subLayerIds,
      checkedNodes,
      layerLookup
    );
  } else {
    results.push(layerInfo.id);
  }

  return results;
}

function _addSubLayers (
  results,
  topId,
  childLayerIds,
  checkedNodes,
  layerLookup
) {
  if (!childLayerIds || !childLayerIds.length) {
    return;
  }

  for (let i = 0; i < childLayerIds.length; i++) {
    addSubLayer(results, topId, childLayerIds[i], checkedNodes, layerLookup);
  }
}

function addSubLayer (results, topId, childLayerId, checkedNodes, layerLookup) {
  const childIsChecked = checkedNodes.indexOf('' + (childLayerId + 1)) > -1;
  let curLayerInfo = layerLookup[childLayerId];
  const isParent = curLayerInfo.subLayerIds;
  let inactiveParent = false;

  while (
    !inactiveParent &&
    curLayerInfo.parentLayerId !== -1 &&
    curLayerInfo.parentLayerId !== topId
  ) {
    inactiveParent =
      checkedNodes.indexOf('' + (curLayerInfo.parentLayerId + 1)) < 0;
    curLayerInfo = layerLookup[curLayerInfo.parentLayerId];
  }

  if (childIsChecked && !isParent && !inactiveParent) {
    results.push(childLayerId);
  }

  if (!inactiveParent && isParent) {
    _addSubLayers(results, topId, isParent, checkedNodes, layerLookup);
  }
}

function _updateVisibleLayers (visibleLayers, updatedLayerIds, isChecked) {
  let curId;
  let i;

  if (isChecked) {
    for (i = 0; i < updatedLayerIds.length; i++) {
      curId = updatedLayerIds[i];

      if (visibleLayers.indexOf(curId) < 0) {
        visibleLayers.push(curId);
      }
    }
  } else {
    for (i = 0; i < updatedLayerIds.length; i++) {
      curId = updatedLayerIds[i];

      const curIndex = visibleLayers.indexOf(curId);

      if (curIndex > -1) {
        visibleLayers.splice(curIndex, 1);
      }
    }
  }
}

export default LayersManager;
